import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link, useParams } from 'react-router-dom';
import { db } from '../db/db';
import { StatusBadge } from '../components/StatusBadge';
import { useJobStatusSync } from '../hooks/useJobStatusSync';

type SortMode = 'alphabetical' | 'pdf-order';

export function GroupMembersPage() {
  const { jobId, groupId } = useParams<{ jobId: string; groupId: string }>();
  const [sortMode, setSortMode] = useState<SortMode>('alphabetical');

  useJobStatusSync(jobId);

  const group = useLiveQuery(() => (groupId ? db.groups.get(groupId) : undefined), [groupId]);
  const members = useLiveQuery(() => (groupId ? db.subjects.where('groupId').equals(groupId).toArray() : []), [groupId]);

  const sorted = useMemo(() => {
    if (!members) return [];
    const copy = [...members];
    if (sortMode === 'alphabetical') {
      copy.sort((a, b) => a.fullName.localeCompare(b.fullName));
    } else {
      copy.sort((a, b) => a.sourceOrder - b.sourceOrder);
    }
    return copy;
  }, [members, sortMode]);

  if (!group || !members) return <div className="screen screen--centered">Loading…</div>;

  return (
    <div className="screen">
      <header className="app-header">
        <div>
          <Link to={`/jobs/${jobId}`} className="back-link">
            ← Groups
          </Link>
          <h1>{group.name}</h1>
        </div>
        <button
          className="btn btn--ghost"
          onClick={() => setSortMode(m => (m === 'alphabetical' ? 'pdf-order' : 'alphabetical'))}
        >
          Sort: {sortMode === 'alphabetical' ? 'A–Z' : 'PDF order'}
        </button>
      </header>

      <ul className="subject-list subject-list--large-rows">
        {sorted.map(s => {
          const hasWarning = s.importWarnings.some(w => w.severity !== 'info');
          const isDuplicate = s.importWarnings.some(w => w.code === 'DUPLICATE_QR_PAYLOAD');
          return (
            <li key={s.id}>
              <Link to={`/jobs/${jobId}/subjects/${s.id}`} className="subject-row subject-row--large">
                <span className="subject-row-name">{s.fullName}</span>
                <span className="subject-row-tags">
                  {isDuplicate && <span className="tag tag--error">Duplicate QR</span>}
                  <StatusBadge status={s.excluded ? 'excluded' : s.status} warning={hasWarning} />
                </span>
              </Link>
            </li>
          );
        })}
        {sorted.length === 0 && <p className="empty-state-inline">No subjects in this group.</p>}
      </ul>
    </div>
  );
}
